import { useState, useEffect, useCallback } from 'react';
import { format, addDays, subDays } from 'date-fns';
import { nb } from 'date-fns/locale';
import type { ScheduleDay, ScheduleItem, EventType } from '@/types';
import { supabase } from '@/lib/supabase';
import { EVENT_TYPE_CONFIG } from '@/lib/eventTypes';

const seededArrangements = new Set<string>();

function defaultDays(eventDate: string, eventType: EventType): { name: string; date: string }[] {
  const base = new Date(eventDate + 'T12:00:00');
  // Weekday names stay lower case; the UI capitalises them where they stand alone
  const dayName = format(base, 'EEEE', { locale: nb });
  const dayLabel = EVENT_TYPE_CONFIG[eventType].scheduleDayLabel;
  const mainLabel = dayLabel ? `${dayName} - ${dayLabel}` : dayName;

  return [
    { name: format(subDays(base, 1), 'EEEE', { locale: nb }), date: subDays(base, 1).toISOString().split('T')[0] },
    { name: mainLabel, date: eventDate },
    { name: format(addDays(base, 1), 'EEEE', { locale: nb }), date: addDays(base, 1).toISOString().split('T')[0] },
  ];
}

function seedDays(eventDate: string, eventType: EventType): Omit<ScheduleDay, 'createdAt'>[] {
  return defaultDays(eventDate, eventType).map((d) => ({ id: crypto.randomUUID(), ...d }));
}

/** Moves the seeded default days along with a changed event date; days the user has renamed or redated stay put */
export async function moveDefaultDays(
  arrangementId: string,
  eventType: EventType,
  fromDate: string,
  toDate: string,
): Promise<{ error: string | null }> {
  const from = defaultDays(fromDate, eventType);
  const to = defaultDays(toDate, eventType);
  const { data, error } = await supabase.from('kjoreplan_days').select('id,name,date').eq('arrangement_id', arrangementId);
  if (error) return { error: error.message };
  const results = await Promise.all(
    (data ?? []).flatMap((day) => {
      const i = from.findIndex((d) => d.name === day.name && d.date === day.date);
      return i === -1 ? [] : [supabase.from('kjoreplan_days').update(to[i]).eq('id', day.id)];
    }),
  );
  return { error: results.find((r) => r.error)?.error?.message ?? null };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function dayFromDb(row: any): ScheduleDay {
  return { id: row.id, name: row.name, date: row.date ?? undefined, createdAt: row.created_at };
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function itemFromDb(row: any): ScheduleItem {
  return {
    id: row.id,
    dayId: row.day_id,
    time: row.time,
    title: row.title,
    location: row.location ?? undefined,
    description: row.description ?? undefined,
    createdAt: row.created_at,
  };
}

async function getUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user?.id ?? null;
}

export function useSchedule(arrangementId: string, eventDate?: string, eventType?: EventType) {
  const [days, setDays] = useState<ScheduleDay[]>([]);
  const [items, setItems] = useState<ScheduleItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!arrangementId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const [daysRes, itemsRes] = await Promise.all([
      supabase
        .from('kjoreplan_days')
        .select('*')
        .eq('arrangement_id', arrangementId)
        .order('date', { ascending: true })
        .order('created_at', { ascending: true }),
      supabase.from('kjoreplan_items').select('*').order('time', { ascending: true }),
    ]);

    if (daysRes.data) {
      if (daysRes.data.length === 0 && eventDate && eventType) {
        // StrictMode runs the effect twice in dev, so only the first run may seed
        const userId = seededArrangements.has(arrangementId) ? null : await getUserId();
        if (userId && !seededArrangements.has(arrangementId)) {
          seededArrangements.add(arrangementId);
          const now = new Date().toISOString();
          const seed = seedDays(eventDate, eventType);
          const rows = seed.map((d) => ({
            id: d.id,
            arrangement_id: arrangementId,
            user_id: userId,
            name: d.name,
            date: d.date ?? null,
            created_at: now,
          }));
          const { error } = await supabase.from('kjoreplan_days').insert(rows);
          if (error) seededArrangements.delete(arrangementId);
          else setDays(seed.map((d) => ({ ...d, createdAt: now })));
        }
      } else {
        setDays(daysRes.data.map(dayFromDb));
      }
    }

    if (itemsRes.data) {
      // Filter items to only those belonging to this arrangement's days
      const dayIds = new Set(daysRes.data?.map((d) => d.id) ?? []);
      setItems(itemsRes.data.filter((i) => dayIds.has(i.day_id)).map(itemFromDb));
    }

    setLoading(false);
  }, [arrangementId, eventDate, eventType]);

  useEffect(() => {
    load();
  }, [load]);

  const sortedDays = [...days].sort((a, b) => {
    if (a.date && b.date) return a.date.localeCompare(b.date);
    return a.createdAt.localeCompare(b.createdAt);
  });

  function itemsForDay(dayId: string) {
    return items.filter((i) => i.dayId === dayId).sort((a, b) => a.time.localeCompare(b.time));
  }

  async function addDay(name: string, date?: string): Promise<string> {
    const userId = await getUserId();
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const day: ScheduleDay = { id, name, date, createdAt: now };
    setDays((prev) => [...prev, day]);
    if (userId) {
      await supabase.from('kjoreplan_days').insert({
        id,
        arrangement_id: arrangementId,
        user_id: userId,
        name,
        date: date ?? null,
      });
    }
    return id;
  }

  async function updateDay(id: string, updates: Partial<Pick<ScheduleDay, 'name' | 'date'>>) {
    setDays((prev) => prev.map((d) => (d.id === id ? { ...d, ...updates } : d)));
    await supabase
      .from('kjoreplan_days')
      .update({
        ...(updates.name !== undefined && { name: updates.name }),
        ...(updates.date !== undefined && { date: updates.date ?? null }),
      })
      .eq('id', id);
  }

  async function deleteDay(id: string) {
    setDays((prev) => prev.filter((d) => d.id !== id));
    setItems((prev) => prev.filter((i) => i.dayId !== id));
    await supabase.from('kjoreplan_days').delete().eq('id', id);
    // Items are deleted by CASCADE in DB
  }

  async function addItem(dayId: string, time: string, title: string, location?: string, description?: string) {
    const userId = await getUserId();
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const item: ScheduleItem = { id, dayId, time, title, location, description, createdAt: now };
    setItems((prev) => [...prev, item]);
    if (userId) {
      const { error } = await supabase.from('kjoreplan_items').insert({
        id,
        day_id: dayId,
        user_id: userId,
        time,
        title,
        location: location ?? null,
        description: description ?? null,
      });
      if (error) setItems((prev) => prev.filter((i) => i.id !== id));
    }
  }

  async function updateItem(
    id: string,
    updates: Partial<Pick<ScheduleItem, 'time' | 'title' | 'location' | 'description'>>,
  ) {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...updates } : i)));
    await supabase
      .from('kjoreplan_items')
      .update({
        ...(updates.time !== undefined && { time: updates.time }),
        ...(updates.title !== undefined && { title: updates.title }),
        ...(updates.location !== undefined && { location: updates.location ?? null }),
        ...(updates.description !== undefined && { description: updates.description ?? null }),
      })
      .eq('id', id);
  }

  async function deleteItem(id: string) {
    setItems((prev) => prev.filter((i) => i.id !== id));
    await supabase.from('kjoreplan_items').delete().eq('id', id);
  }

  return { days: sortedDays, loading, itemsForDay, addDay, updateDay, deleteDay, addItem, updateItem, deleteItem };
}
